import { Component, inject, isDevMode, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, RouterOutlet } from '@angular/router';
import { AuthserviceService } from './content/pages-new-design/auth-page/login-page/authservice.service';
import { Location, LocationStrategy, PathLocationStrategy, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { CompoApp } from './services/CompoApp';
import { PrimeNGConfig } from 'primeng/api';
import Constants from './Constants';
import { Title } from '@angular/platform-browser';
import { SseSingletonService } from './services/sse/sse-singleton.service';
import { ToastModule } from 'primeng/toast';
import { MessageModule } from 'primeng/message';
import { BlockUIModule } from 'primeng/blockui';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
import { fad } from '@fortawesome/pro-duotone-svg-icons';
import { LOGOUT_TRIGGER } from './services/client/UnsubscribeBaseService.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IS_AUTHENTICATED } from './shared.app.auth.context';
import { SELECTED_RIGHTS_ROLE, USER_ID, USER_ORG_ID } from './shared.app.rights.context';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }],
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    ToastModule,
    MessageModule,
    RouterOutlet,
    BlockUIModule,
    ProgressSpinnerModule,
    TranslateModule
  ]
})
export class AppComponent implements OnInit, OnDestroy {
  locationUrl: string;
  isBrowserIncompatible: boolean;

  private sseSingletonService = inject(SseSingletonService);

  isDevMode: boolean = isDevMode();

  readonly #faIconLibrary = inject(FaIconLibrary);

  //region LIFE CYCLE
  constructor(private translate: TranslateService,
              private router: Router,
              public app: CompoApp,
              public titleService: Title,
              private primeNgConfig: PrimeNGConfig,
              location: Location,
              authService: AuthserviceService) {

    this.#faIconLibrary.addIconPacks(fas);
    this.#faIconLibrary.addIconPacks(fal);
    this.#faIconLibrary.addIconPacks(far);
    this.#faIconLibrary.addIconPacks(fab);
    this.#faIconLibrary.addIconPacks(fad);

    registerLocaleData(localeDe);
    // this language will be used as a fallback when a translation isn't found in the current language
    translate.setDefaultLang('en');
    translate.onLangChange.subscribe(e => {
      this.onLanguageChanged(e);
      primeNgConfig.setTranslation(app.i18n.data.primeng);
    });

    // 1. get the last selected language
    let lang = app.data.getLang();

    //2. If none was found, use the browser default
    if (!lang) {
      lang = translate.getBrowserLang();
      this.app.data.setBrowserLangCode(lang);
    }

    LOGOUT_TRIGGER
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        authService.logout();
      });

    //3. the lang to use, if the lang isn't available, it will use the current loader to get them
    translate.use(lang);

    primeNgConfig.setTranslation(app.i18n.data?.primeng);

    /*
     * Wenn der User nicht eingeloggt ist, und ruft ein url in der Applikation auf,
     * wird diese url im localstorage gespeichert, der User wird zurückgeleitet zu Login Seite.
     * Nach erfolgreiche Login wird der User auf der original Url weitergeleitet.
     */

    const browserURL: string[] = location.path().split('?');
    this.locationUrl = browserURL[0];
    let queryParams: any = null;
    if (browserURL.length > 1) {
      const normalizedQueryParams: string = Location.normalizeQueryParams(browserURL[1]);
      queryParams = Object.fromEntries(new URLSearchParams(normalizedQueryParams));
    }

    if (!authService.isAuthenticated()) {
      this.app.data.setAccessMode(null);
      this.app.data.setUser(null);
      USER_ID.next(null);
      USER_ORG_ID.next(null);
      SELECTED_RIGHTS_ROLE.next(null);
      IS_AUTHENTICATED.next(false);
      this.app.data.setSelectedUserRole(null);
      if (!authService.isPublicUrl()) {
        this.app.data.setPendingUrl(this.locationUrl);
        router.navigate(['']);
      }
    } else if (this.app.data.getUser()) {
      authService.authenticateWithToken();
      this.app.data.setPendingUrl(null);
      if (this.locationUrl === '') {
        router.navigate(['pages/dashboard'], { queryParams });
      } else {
        router.navigate([this.locationUrl], { queryParams });
      }
      this.sseSingletonService.connect();
    } else {
      console.dir("else");
    }

    if (!this.sseSingletonService.isConnected()) {
      this.sseSingletonService.connect();
    }
  }

  public setTitle() {
    this.titleService.setTitle('Objack');
  }

  ngOnInit(): void {
    // noinspection JSUnusedLocalSymbols
    window.onbeforeunload = ev => this.sseSingletonService.unload();
    window.onload = ev => {
    };
    // check for deprecated local storage values
    this.cleanUpDeprecatedLocalStorageItems();
    this.setTitle();
  }

  ngOnDestroy(): void {
  }

  //endregion
  onLanguageChanged(data) {
    this.app.i18n.data = data.translations;
  }


  // check for some deprecated key's in the local storage
  private cleanUpDeprecatedLocalStorageItems(): void {
    const deprecatedEnumValues: any[] = Constants.DEPRECATED_ENUM_VALUES;
    // check for filter
    const filterSuffix = 'savedFilters_';

    for (const deprecatedEnumValue of deprecatedEnumValues) {
      // check if some filter with the old value exist
      const oldFilter = localStorage.getItem(filterSuffix + deprecatedEnumValue.oldValue);
      if (oldFilter) {
        // remove the old key
        localStorage.removeItem(filterSuffix + deprecatedEnumValue.oldValue);
        // add the saved value with new key
        localStorage.setItem(filterSuffix + deprecatedEnumValue.newValue, oldFilter);
      }
    }
  }
}
